<template>
  <div class="provider-report-page">
    <div class="flex justify-center mb-4">
      <img
        src="../../assets/Provider-logos/SW-Logo-2.svg"
        alt="sw"
        height="80px"
        class="lazyload"
      />
    </div>
    <p class="text-center list-header">
      What could I do with my Scottish Widows pension?
    </p>
    <p>
      As an AgeWage customer, you know the value you have received for your
      money – it’s in your AgeWage score and you can read how to interpret your
      score by pressing the links on your pension dashboard.
    </p>

    <p>
      The other part of the value for money equation is the quality of service
      you get from Scottish Widows. We have made our own assessment of the
      quality of service AgeWage customers have received from Scottish Widows.
      Scottish Widows has provided a
      <span class="green--text"> good </span>service to AgeWage.
    </p>
    <p>
      But you are the best judge of quality. To make your judgement we suggest
      that you speak with the Scottish Widows customer service teams. They can
      help you work out the quality of service you are likely to get in the
      future.
    </p>
    <p>
      Your best way to explore your Scottish Widows pension is using&nbsp;<a
        href="https://www.scottishwidows.co.uk/existingcustomers/"
        target="_blank"
      >
        the existing customer homepage .</a
      >
    </p>

    <v-divider class="seperator-line"></v-divider>
    <p class="list-header">
      What kind of Scottish Widows pension do you have?
    </p>
    <p>
      Scottish Widows is a mixture of policies that have always been with
      Scottish Widows and those that have moved to Scottish Widows from Zurich
      Assurance (formerly Eagle Star).
    </p>
    <p>
      They divide between workplace pensions (where your employer pays in) and
      personal pensions where just you pay in.
    </p>
    <p>
      To help you understand what pension you have, Scottish Widows have a
      number of helplines
    </p>
    <table
      class="table table-style1 table-xs-stack text-left bg-primary3 mt30-0"
    >
      <tbody>
        <tr>
          <td class="hasTH" data-th="Description for pension or an annuity">
            If you took your policy out through your employer
          </td>
          <td class="hasTH" data-th="Number to call">
            <a href="tel:03457556557">0345 755 6557</a>
          </td>
          <td class="hasTH" data-th="Opening hours">Mon to Fri 9am–5pm</td>
        </tr>
        <tr>
          <td class="hasTH" data-th="Description for pension or an annuity">
            If your product is called “Retirement Account”
          </td>
          <td class="hasTH" data-th="Number to call">
            <a href="tel:03457166733">0345 716 6733</a>
          </td>
          <td class="hasTH" data-th="Opening hours">Mon to Fri 8am-6pm</td>
        </tr>
        <tr>
          <td class="hasTH" data-th="Description for pension or an annuity">
            If you took your policy out via an IFA or direct with Scottish
            Widows
          </td>
          <td class="hasTH" data-th="Number to call">
            <a href="tel:03457166777">0345 716 6777</a>
          </td>
          <td class="hasTH" data-th="Opening hours">Mon to Fri 8am–6pm</td>
        </tr>
        <tr>
          <td class="hasTH" data-th="Description for pension or an annuity">
            If you took your policy out through a branch (formerly Lloyds TSB)
          </td>
          <td class="hasTH" data-th="Number to call">
            <a href="tel:03453004455">0345 300 4455</a>
          </td>
          <td class="hasTH" data-th="Opening hours">Mon to Fri 9am–5pm</td>
        </tr>
        <tr>
          <td class="hasTH" data-th="Description for pension or an annuity">
            If you are 55 or over and wish to explore your retirement options
          </td>
          <td class="hasTH" data-th="Number to call">
            <a href="tel:03458356644">0345 835 6644</a>
          </td>
          <td class="hasTH" data-th="Opening hours">Mon to Fri 8am–6pm</td>
        </tr>
      </tbody>
    </table>
    <p>
      You&nbsp; may need your policy number to identify what kind of policy you
      have.&nbsp; You should be able to find your policy number by checking your
      document page on
      <a href="http://www.agewage.com/" rel="nofollow" target="_blank"
        >http://www.agewage.com</a
      >
    </p>
    <p>
      But if you are confused and want to know where to go, Scottish Widows have
      a general phone helpline
    </p>
    <table
      class="table table-style1 table-xs-stack text-left bg-primary3 mt30-0"
    >
      <tbody>
        <tr>
          <td><strong>I’m not sure what I have!</strong></td>
          <td class="hasTH" colspan="2" data-th="Number to call">
            <strong
              >Please call&nbsp;<a href="tel:03457166777"
                >0345 716 6777</a
              ></strong
            >
          </td>
        </tr>
      </tbody>
    </table>
    <v-divider class="seperator-line"></v-divider>
    <p class="list-header">
      Is your Scottish Widows Pension worth keeping?
    </p>
    <p>
      The Pensions Regulator suggest 6 factors that make for a good pension .
    </p>
    <ul>
      <li>
        Enough money gets contributed
      </li>
      <li>The right investment options</li>
      <li>Proper record keeping</li>
      <li>Value for money (VFM)</li>
      <li>Protecting your assets</li>
      <li>Full access to pension freedoms</li>
    </ul>
    <br />
    <p class="list-header">
      How Scottish Widows helps you work out how much to contribute
    </p>
    <p>
      Scottish Widows has a range of retirement planning services
      <a
        href="https://www.scottishwidows.co.uk/retirement/calculators-tools/"
        target="_blank"
        >you can access here</a
      >
    </p>
    <p class="list-header">
      How Scottish Widows helps your find the right investment options
    </p>
    <p>
      You can&nbsp;find out&nbsp;<a
        href="https://www.scottishwidows.co.uk/retirement/retirement-explained/basics/funds-and-investing/"
        target="_blank"
        >your investment options here</a
      >
    </p>
    <p class="list-header">
      How Scottish Widows manages your data
    </p>
    <p>
      Our rating for the way that Scottish Widows has managed data with AgeWage
      is <span class="green--text">green/good. </span>
    </p>
    <p class="list-header">
      The value you get for the money you send to Scottish Widows
    </p>
    <p>
      Please refer to your AgeWage score and the explanation we’ve provided on
      how it helps you understand VFM. Make sure to read the notes we’ve
      provided on how to use your AgeWage score.
    </p>
    <p class="list-header">
      Protecting your assets
    </p>
    <p>
      Scottish Widows offer certain options to reduce the risks you take being
      invested in the market. You can find out about your attitude to risk,
      <a
        href="https://www.scottishwidows.co.uk/retirement/retirement-explained/basics/risk-and-return/"
        target="_blank"
        >follow this link.</a
      >
    </p>
    <p class="list-header">
      Options to use the pension freedoms
    </p>
    <p>
      Scottish Widows offers support to help you work out your retirement
      options.
      <a
        href="https://www.scottishwidows.co.uk/retirement/retirement-explained/taking/pension-options/"
        target="_blank"
        >You can read about the options here</a
      >
    </p>
    <p>
      You can find out about the options for your specific Scottish Widows
      pension by registering&nbsp;
      <a href="https://www.scottishwidows.co.uk/register/" target="_blank"
        >here for workplace pensions</a
      >
      and here for<a
        href="https://www.scottishwidows.co.uk/register/"
        target="_blank"
      >
        personal pensions&nbsp;</a
      >
    </p>
    <p>
      Each type of pension offered by Scottish Widows could have different
      retirement options.
    </p>
    <v-divider class="seperator-line"></v-divider>
    <p class="list-header">
      Transferring pension pots to Scottish Widows
    </p>
    <p>
      You may feel that you would like to transfer other pension pots to
      Scottish Widows. If you would like to, follow the link below:
    </p>
    <p class="list-header text-center">
      <a
        href="https://www.scottishwidows.co.uk/retirement/pension-transfers/"
        target="_blank"
      >
        Transfer other pots to Scottish Widows here
      </a>
    </p>
  </div>
</template>
<script>
export default {
  layout: 'x-dashboard'
};
</script>

<style scoped>
table {
  border: 1px solid #e7e7e7;
  margin: 0 -1px 24px 0;
  text-align: left;
  width: 100%;
}
table td {
  border-top: 1px solid #e7e7e7;
  padding: 6px 10px;
}
</style>
